import { useState, useRef } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { BEFORE_AFTER } from '@/data/site';

export default function BeforeAfter() {
  const item = BEFORE_AFTER[0];
  const ref = useRef(null);
  const x = useMotionValue(50);
  const clip = useTransform(x, (v) => `inset(0 ${100 - v}% 0 0)`);
  const [dragging, setDragging] = useState(false);

  const handle = (clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    x.set(Math.max(0, Math.min(100, pct)));
  };

  return (
    <section className="relative bg-[#FAFAF5] py-28 md:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-12">
          <div className="md:col-span-4">
            <div className="font-mono-label text-[10px] tracking-[0.28em] uppercase text-[#1D4ED8] flex items-center gap-3">
              <span className="w-8 h-px bg-[#1D4ED8]" /> Before / After
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display uppercase text-[#0A0A0A] text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              {item.title}
            </h2>
            <p className="mt-6 text-[#0A0A0A]/70 max-w-xl">
              Drag the divider. This kitchen went from tired 90s builder-basic to a chef-grade room with a hidden pantry, waterfall island, and integrated appliances — in eleven weeks.
            </p>
          </div>
        </div>

        <div
          ref={ref}
          onMouseDown={() => setDragging(true)}
          onMouseUp={() => setDragging(false)}
          onMouseLeave={() => setDragging(false)}
          onMouseMove={(e) => dragging && handle(e.clientX)}
          onTouchMove={(e) => handle(e.touches[0].clientX)}
          className="relative w-full aspect-[16/9] overflow-hidden select-none border border-black/10"
        >
          <img src={item.before} alt="Before" className="absolute inset-0 w-full h-full object-cover" />
          <motion.div style={{ clipPath: clip }} className="absolute inset-0">
            <img src={item.after} alt="After" className="w-full h-full object-cover" />
          </motion.div>

          <div className="absolute top-4 left-4 font-mono-label text-[10px] tracking-[0.24em] uppercase text-[#0A0A0A] bg-black/60 px-3 py-1.5 backdrop-blur-sm">
            Before
          </div>
          <div className="absolute top-4 right-4 font-mono-label text-[10px] tracking-[0.24em] uppercase text-[#0A0A0A] bg-[#1D4ED8] px-3 py-1.5">
            After
          </div>

          <motion.div style={{ left: useTransform(x, (v) => `${v}%`) }} className="absolute top-0 bottom-0 -translate-x-1/2 w-px bg-[#1D4ED8]">
            <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-1/2 w-12 h-12 rounded-full bg-[#1D4ED8] grid place-items-center text-[#0A0A0A] font-mono-label text-[10px] tracking-[0.2em] shadow-2xl">
              ↔
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
